import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  HistoryCloserSlotDTO,
  HistorySlotStatus,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import { dayLabel } from "./closerAvailabilityHistoryStats.ts";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Europe/Rome";

const STATUS_LABEL: Record<HistorySlotStatus, string> = {
  free: "libero",
  busy_work: "call",
  busy_ooo: "OOO",
};

type Cell = { className: string; label: string };

function cellOf(slot: HistoryCloserSlotDTO): Cell {
  const { opening: from, closure: to } = slot;
  if (!from && !to)
    return {
      className: "bg-slate-50 border border-dashed border-slate-300",
      label: "non presente",
    };
  if (!from)
    return {
      className: "bg-white ring-2 ring-inset ring-blue-500",
      label: `non presente all'apertura · ${STATUS_LABEL[to!]}`,
    };
  if (!to)
    return {
      className: "bg-slate-50 border border-dashed border-slate-300",
      label: "non più presente",
    };
  const key = `${from}>${to}`;
  switch (key) {
    case "free>free":
      return { className: "bg-green-100", label: "libero" };
    case "busy_work>busy_work":
      return { className: "bg-blue-100", label: "call già all'apertura" };
    case "free>busy_work":
      return { className: "bg-blue-500", label: "prenotato nel periodo" };
    case "busy_ooo>busy_ooo":
      return { className: "bg-gray-200", label: "OOO già all'apertura" };
    case "free>busy_ooo":
      return { className: "bg-red-500", label: "bloccato dopo l'apertura" };
    case "busy_work>busy_ooo":
      return { className: "bg-red-800", label: "call saltata e slot bloccato" };
    case "busy_work>free":
      return {
        className: "bg-green-100 ring-2 ring-inset ring-blue-500",
        label: "call cancellata, slot libero",
      };
    default:
      return {
        className: "bg-white ring-2 ring-inset ring-green-600",
        label: "sbloccato",
      };
  }
}

const SLOT_GRID_LEGEND: [string, string][] = [
  ["bg-green-100", "Libero"],
  ["bg-blue-100", "Call già all'apertura"],
  ["bg-blue-500", "Prenotato nel periodo"],
  ["bg-red-500", "Bloccato dopo l'apertura"],
  ["bg-red-800", "Call saltata + bloccato"],
  ["bg-white ring-2 ring-inset ring-green-600", "Sbloccato"],
  ["bg-gray-200", "OOO già all'apertura"],
  ["bg-white ring-2 ring-inset ring-blue-500", "Non presente all'apertura"],
];

export function CloserSlotGrid({ slots }: { slots: HistoryCloserSlotDTO[] }) {
  if (slots.length === 0) {
    return <p className="text-sm text-gray-400">Nessuno slot.</p>;
  }

  const byKey = new Map<string, HistoryCloserSlotDTO>();
  const dates = new Set<string>();
  const times = new Set<string>();
  for (const slot of slots) {
    const start = dayjs(slot.slotStart).tz(TZ);
    const date = start.format("YYYY-MM-DD");
    const time = start.format("HH:mm");
    dates.add(date);
    times.add(time);
    byKey.set(`${date} ${time}`, slot);
  }
  const sortedDates = [...dates].sort();
  const sortedTimes = [...times].sort();

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <div
          className="grid gap-0.5 text-[11px] tabular-nums min-w-[360px]"
          style={{
            gridTemplateColumns: `44px repeat(${sortedDates.length}, minmax(32px, 1fr))`,
          }}
        >
          <div />
          {sortedDates.map((d) => (
            <div key={d} className="text-center font-medium text-gray-500 pb-1">
              {dayLabel(d)}
            </div>
          ))}
          {sortedTimes.map((time) => (
            <div key={time} className="contents">
              <div className="text-right pr-1.5 text-gray-400 leading-4">
                {time.endsWith(":00") ? time : ""}
              </div>
              {sortedDates.map((date) => {
                const slot = byKey.get(`${date} ${time}`);
                if (!slot) return <div key={date} className="h-4" />;
                const cell = cellOf(slot);
                const title = slot.eventTitle ? ` · "${slot.eventTitle}"` : "";
                return (
                  <div
                    key={date}
                    className={`h-4 rounded-sm ${cell.className}`}
                    title={`${dayLabel(date)} ${time} – ${cell.label}${title}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-gray-500">
        {SLOT_GRID_LEGEND.map(([className, label]) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${className}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
